import { useEffect, useRef } from 'react';
import type { Message } from '../api/client';

/**
 * Lands a thread on the first message its reader has not been shown.
 *
 * **Both doors, one behaviour.** A member's screen and a parent's already share `useConversation`
 * and `ParticipantStrip`; a second copy of "where does this thread open" is how they would start to
 * differ in manners even while rule 13b keeps their rules identical.
 *
 * ## The ordering that makes this work
 *
 * `markRead` fires as soon as the thread is visible, so the boundary has to be captured **before**
 * that lands. It is taken once, from the first detail that arrives, and then held: if it followed
 * the marker it would chase the reader down the page, and every thread would open at the bottom —
 * which looks like the feature not working rather than a race, and would be maddening to diagnose.
 *
 * Scrolling is `scrollIntoView` rather than a computed offset, because message heights vary with
 * body length and an offset would be wrong for exactly the long messages somebody most wants to
 * land on.
 */
/**
 * Run something once the browser has had a chance to lay the page out.
 *
 * `requestAnimationFrame` is the right tool when the tab is on screen, and it **never fires at all**
 * when it is not — a hidden tab does not paint, so it does not animate. That is not a corner case
 * here: it is exactly the state a Claude-in-Chrome tab is always in, so anything scheduled on rAF
 * alone cannot be exercised under browser automation, and shadowing `document.visibilityState` does
 * not help because the throttling follows the real tab rather than the property.
 *
 * It is also a real state for a real person: a thread opened in a background tab would render and
 * never land anywhere. So a hidden document falls back to a timer, which does fire.
 */
function schedule(fn: () => void) {
  if (document.hidden) {
    window.setTimeout(fn, 0);
    return;
  }

  requestAnimationFrame(fn);
}

export function useUnreadAnchor(
  conversationId: string | null | undefined,
  messages: Message[],
  lastReadSeq: number | null | undefined,
  /** The reader. Their own messages are never what they have "not read yet". */
  meId: string,
) {
  /** The boundary as it was when the thread opened. `undefined` until the detail arrives. */
  const boundary = useRef<number | null | undefined>(undefined);
  const landed = useRef<string | null>(null);
  const firstUnread = useRef<HTMLDivElement | null>(null);
  const bottom = useRef<HTMLDivElement | null>(null);

  /** Which thread the two refs above are describing. */
  const forThread = useRef<string | null | undefined>(undefined);

  // A different thread is a fresh landing. Without this, opening a second thread from the list
  // would keep the first one's boundary and its "already landed" flag, and never scroll again.
  //
  // **In render, not in an effect**, and that is not a style choice. As an effect it ran AFTER the
  // render that captured the boundary below, wiping it back to undefined on mount -- so the scroll
  // effect that ran next always saw "no boundary yet" and returned. The thread never moved, and it
  // looked for all the world like scrollIntoView not working.
  if (forThread.current !== conversationId) {
    forThread.current = conversationId;
    boundary.current = undefined;
    landed.current = null;
  }

  // Captured on the first detail and then frozen. `lastReadSeq` is `undefined` while the detail is
  // in flight and `null` for somebody who has never opened the thread — a real difference, so the
  // check is against `undefined` rather than falsiness.
  if (boundary.current === undefined && lastReadSeq !== undefined) {
    boundary.current = lastReadSeq;
  }

  const seq = boundary.current;

  /**
   * The first message this reader has not been shown, or null when they are caught up.
   *
   * A reader with no marker at all has read nothing, so the first message is the boundary — which
   * is also where a thread they have never opened should start.
   */
  /**
   * Your own messages are never new to you.
   *
   * Without this, posting puts the New line above the thing you just typed — the boundary is
   * frozen at open (deliberately, so it does not chase you down the page), so anything after it
   * counts as unread including your own words. It reads as the product not knowing who is holding
   * the phone.
   *
   * Skipping them rather than advancing the boundary keeps the other case right: if somebody else
   * posted while you were reading and *then* you replied, the line still belongs above theirs.
   */
  const isNewToMe = (m: Message, after: number) => m.seq > after && m.authorPersonId !== meId;

  const firstUnreadId =
    seq === undefined
      ? null
      : seq === null
        ? (messages.find((m) => m.authorPersonId !== meId)?.id ?? null)
        : (messages.find((m) => isNewToMe(m, seq))?.id ?? null);

  useEffect(() => {
    if (!conversationId || messages.length === 0 || boundary.current === undefined) {
      return;
    }

    // Once per thread. A message arriving while somebody is reading must not yank the page.
    if (landed.current === conversationId) {
      return;
    }

    landed.current = conversationId;

    /**
     * Scroll, then check it took, and try again if it did not.
     *
     * One `requestAnimationFrame` is not enough and the reason is worth writing down: forty-six
     * message bubbles are inserted in one go and their heights depend on their bodies, so on the
     * frame after React commits them the document is still barely taller than the viewport. There
     * is nowhere to scroll *to* yet, `scrollIntoView` is a no-op, and the page settles at the top
     * once the real heights resolve -- which reads exactly like the feature being broken.
     *
     * Verifying rather than adding a longer delay: a fixed timeout is a guess about how slow a
     * device is, and this is a fact we can check.
     */
    let attempts = 0;

    const land = () => {
      const target = firstUnread.current;

      if (target) {
        // Centred rather than at the top, so the last thing they had read stays visible above it.
        // A boundary with nothing above it reads as a jump into the middle of nowhere.
        target.scrollIntoView({ block: 'center' });

        const box = target.getBoundingClientRect();

        if (box.top >= 0 && box.top <= window.innerHeight) {
          return;
        }
      } else {
        // Caught up: the bottom of the PAGE, not the bottom of the message list. Scrolling the
        // last message to the viewport's edge leaves the composer below the fold, which is the
        // one control somebody arriving at a thread they have read is most likely to want.
        window.scrollTo({ top: document.body.scrollHeight });

        if (window.scrollY >= document.body.scrollHeight - window.innerHeight - 4) {
          return;
        }
      }

      if (attempts < 10) {
        attempts++;
        schedule(land);
      }
    };

    schedule(land);
    // `lastReadSeq` is in here and it is load-bearing: the detail and the messages are two separate
    // fetches, and when the detail lands SECOND there is no further change to `messages` to wake
    // this effect. Without it the thread simply never scrolled, and only on the threads long enough
    // to have somewhere to scroll to -- which is every thread that needed it.
  }, [conversationId, messages, lastReadSeq]);


  return { firstUnreadId, firstUnread, bottom };
}
